import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import jsQR from "jsqr"

export default class extends Controller {
  static targets = ["modal", "video", "canvas", "status"]
  static values = {
    scanningText: String,
    unsupportedText: String,
    deniedText: String,
    invalidText: String,
    foundText: String
  }

  connect() {
    this.stop = this.stop.bind(this)
    this.modalTarget.addEventListener("hidden.bs.modal", this.stop)
  }

  disconnect() {
    this.modalTarget.removeEventListener("hidden.bs.modal", this.stop)
    this.stop()
    this.modal?.dispose()
  }

  open() {
    this.instance.show()
    this.start()
  }

  async start() {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.setStatus(this.unsupportedTextValue)
      return
    }

    this.stop()
    this.setStatus(this.scanningTextValue)

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" }
        }
      })

      this.videoTarget.srcObject = this.stream
      await this.videoTarget.play()
      this.scanning = true
      this.scanFrame()
    } catch (error) {
      this.setStatus(error.name === "NotAllowedError" ? this.deniedTextValue : this.unsupportedTextValue)
    }
  }

  stop() {
    this.scanning = false
    cancelAnimationFrame(this.frameRequest)
    clearTimeout(this.frameRequest)

    this.stream?.getTracks().forEach((track) => track.stop())
    this.stream = null

    if (this.hasVideoTarget) {
      this.videoTarget.pause()
      this.videoTarget.srcObject = null
    }
  }

  scanFrame() {
    if (!this.scanning) return

    if (this.videoTarget.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      const width = this.videoTarget.videoWidth
      const height = this.videoTarget.videoHeight

      if (width > 0 && height > 0) {
        const canvas = this.canvasTarget
        const context = canvas.getContext("2d", { willReadFrequently: true })

        canvas.width = width
        canvas.height = height
        context.drawImage(this.videoTarget, 0, 0, width, height)

        const imageData = context.getImageData(0, 0, width, height)
        const code = jsQR(imageData.data, width, height)

        if (code?.data) {
          this.handleCode(code.data)
          return
        }
      }
    }

    this.frameRequest = requestAnimationFrame(() => this.scanFrame())
  }

  handleCode(value) {
    const joinUrl = this.joinUrlFor(value)

    if (joinUrl) {
      this.setStatus(this.foundTextValue)
      this.stop()
      window.location.assign(joinUrl)
    } else {
      this.setStatus(this.invalidTextValue)
      this.frameRequest = window.setTimeout(() => this.scanFrame(), 1000)
    }
  }

  joinUrlFor(value) {
    try {
      const url = new URL(value.trim(), window.location.origin)
      const path = url.pathname.replace(/\/+$/, "")

      if (url.origin === window.location.origin && /^\/join\/[^/]+$/.test(path)) {
        return `${path}${url.search}${url.hash}`
      }
    } catch (_error) {
      return null
    }

    return null
  }

  setStatus(message) {
    this.statusTarget.textContent = message
  }

  get instance() {
    this.modal ||= Modal.getOrCreateInstance(this.modalTarget)
    return this.modal
  }
}
